import type { ReactNode } from 'react'
import { ArrowDown, ArrowUp, CircleX, Info, PackageMinus, Plus } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { formatPoundsDifference } from '../../utils/currency'
import {
  BADGE_TONE_CLASSES,
  BadgeTone,
  CHANGE_KIND_MARKS,
  CHANGE_TONE_CLASSES,
  ChangeTone,
  NON_SUPPLY_SCANNER_LABEL,
  SupplierChangeKind,
} from '../../constants/supplierChanges'

// Pieces shared by the "Supplier changes to review" tab and an order's
// supplier changes page (PO-008 / PO-009), drawn as the mock's Option J.

const KIND_ICONS: Record<SupplierChangeKind, LucideIcon> = {
  [SupplierChangeKind.PriceIncreased]: ArrowUp,
  [SupplierChangeKind.PriceDecreased]: ArrowDown,
  [SupplierChangeKind.QuantityIncreased]: ArrowUp,
  [SupplierChangeKind.QuantityReduced]: PackageMinus,
  [SupplierChangeKind.Cancelled]: CircleX,
  [SupplierChangeKind.Removed]: CircleX,
  [SupplierChangeKind.Added]: Plus,
}

/** The supplier's change to the total, said aloud with what it is. */
export function SupplierChangeMoney({ amount }: { amount: string }) {
  const value = parseFloat(amount)
  return (
    <>
      {value === 0 ? 'No change' : formatPoundsDifference(value)}
      <span className="sr-only"> supplier&apos;s change to the total</span>
    </>
  )
}

/** A chip with an icon and words, in its tone (Option J's status marks). */
function ChangeChip({ icon: Icon, label, tone }: { icon: LucideIcon; label: string; tone: ChangeTone }) {
  const classes = CHANGE_TONE_CLASSES[tone]
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-semibold ${classes.chip}`}>
      <Icon className={`h-3 w-3 flex-shrink-0 ${classes.icon}`} aria-hidden="true" />
      {label}
    </span>
  )
}

/** "Price up", or "Line item cancelled × 2" when several lines carry it. */
export function KindChip({ kind, count = 1 }: { kind: SupplierChangeKind; count?: number }) {
  const mark = CHANGE_KIND_MARKS[kind]
  if (!mark) return null
  return <ChangeChip icon={KIND_ICONS[kind]} label={count > 1 ? `${mark.label} × ${count}` : mark.label} tone={mark.tone} />
}

/** "Non-Supply Scanner product": an added product that isn't in our catalogue. */
export function NonSupplyScannerChip({ count = 1 }: { count?: number }) {
  return <ChangeChip icon={Info} label={count > 1 ? `${NON_SUPPLY_SCANNER_LABEL} × ${count}` : NON_SUPPLY_SCANNER_LABEL} tone={ChangeTone.Product} />
}

/** A small flat badge, as Option J's. */
export function Badge({ tone = BadgeTone.Gray, children }: { tone?: BadgeTone; children: ReactNode }) {
  return <span className={`inline-flex items-center whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[11px] font-semibold ${BADGE_TONE_CLASSES[tone]}`}>{children}</span>
}
